import { compact } from "lodash-es";
import { createReducer } from "typesafe-actions";

import { RawFile } from "@mapmycustomers/shared/types/File";

import { fileCreatedAtDescComparator } from "@app/component/preview/util/comparators";
import RecordPaneTab from "@app/enum/preview/RecordPaneTab";
import FileListItem from "@app/types/FileListItem";

import {
  Actions,
  addActivityCompany,
  addActivityDeal,
  bulkUpdateFiles,
  changeAssociatedEntities,
  clearAllUploadedActivityFiles,
  deleteActivity,
  deleteActivityFile,
  fetchChildActivities,
  fetchPreviewData,
  postponeActivity,
  relateUnrelateEntities,
  removeActivityCompany,
  removeActivityDeal,
  setActiveTab,
  setActivityPeople,
  setHasChangesFlag,
  toggleCompleteActivity,
  updateActivity,
  uploadActivityFiles,
} from "./actions";
import ActivityRecordData from "./ActivityRecordData";

const INITIAL_ASSOCIATIONS = {
  availableCompanies: [],
  availableDeals: [],
  availablePeople: [],
  inaccessibleEntity: false,
};

const INITIAL_RECORD_DATA: Readonly<ActivityRecordData> = {
  activity: undefined,
  associations: INITIAL_ASSOCIATIONS,
  childActivities: [],
  childActivitiesTotal: 0,
  files: [],
};

export interface ActivityRecordState {
  activeTab?: RecordPaneTab;
  activityUpdateLoading: boolean;
  addCompanyLoading: boolean;
  addDealLoading: boolean;
  childActivitiesLoading: boolean;
  deleteLoading: boolean;
  entityRelating: boolean;
  entityRelatingSuccess: boolean;
  filesAdded: FileListItem[];
  filesToDeletedIds: RawFile["id"][];
  filesUploading: boolean;
  hasChanges: boolean;
  loading: boolean;
  postponeLoading: boolean;
  recordData: ActivityRecordData;
  setPeopleLoading: boolean;
  toggleCompleteLoading: boolean;
}

const initialState: ActivityRecordState = {
  activeTab: undefined,
  activityUpdateLoading: false,
  addCompanyLoading: false,
  addDealLoading: false,
  childActivitiesLoading: false,
  deleteLoading: false,
  entityRelating: false,
  entityRelatingSuccess: false,
  filesAdded: [],
  filesToDeletedIds: [],
  filesUploading: false,
  hasChanges: false,
  loading: false,
  postponeLoading: false,
  recordData: INITIAL_RECORD_DATA,
  setPeopleLoading: false,
  toggleCompleteLoading: false,
};

const activityRecord = createReducer<ActivityRecordState, Actions>(initialState)
  .handleAction(addActivityCompany.request, (state) => ({
    ...state,
    addCompanyLoading: true,
  }))
  .handleAction(addActivityCompany.success, (state, { payload }) => ({
    ...state,
    addCompanyLoading: false,
    recordData: {
      ...state.recordData,
      associations: {
        ...state.recordData.associations,
        ...payload.nextAssociations,
        associatedCompany: payload.company,
      },
    },
  }))
  .handleAction(addActivityCompany.failure, (state) => ({
    ...state,
    addCompanyLoading: false,
  }))
  .handleAction(addActivityDeal.request, (state) => ({
    ...state,
    addDealLoading: true,
  }))
  .handleAction(addActivityDeal.success, (state, { payload }) => ({
    ...state,
    addDealLoading: false,
    recordData: {
      ...state.recordData,
      associations: {
        ...state.recordData.associations,
        ...payload.nextAssociations,
        associatedDeal: payload.deal,
      },
    },
  }))
  .handleAction(addActivityDeal.failure, (state) => ({
    ...state,
    addDealLoading: false,
  }))
  .handleAction(setActivityPeople.request, (state) => ({
    ...state,
    setPeopleLoading: true,
  }))
  .handleAction(setActivityPeople.success, (state, { payload }) => ({
    ...state,
    recordData: {
      ...state.recordData,
      // Keep the activity in sync so the Details tab (which reads activity.contacts) reflects the change.
      // contacts is only part of the API contract when the multi-contact add-on is enabled.
      activity: state.recordData.activity
        ? {
            ...state.recordData.activity,
            contact: payload.people[0],
            ...(payload.isMultiContactEnabled ? { contacts: payload.people } : {}),
          }
        : state.recordData.activity,
      associations: {
        ...state.recordData.associations,
        ...payload.nextAssociations,
        associatedPeople: payload.people,
        associatedPerson: payload.people[0],
      },
    },
    setPeopleLoading: false,
  }))
  .handleAction(setActivityPeople.failure, (state) => ({
    ...state,
    setPeopleLoading: false,
  }))
  .handleAction(bulkUpdateFiles.success, (state, { payload }) => ({
    ...state,
    recordData: { ...state.recordData, files: payload },
  }))
  .handleAction(changeAssociatedEntities.request, (state, { payload }) => ({
    ...state,
    recordData: {
      ...state.recordData,
      associations: {
        ...state.recordData.associations,
        ...("company" in payload ? { associatedCompany: payload.company } : {}),
        ...("deal" in payload ? { associatedDeal: payload.deal } : {}),
        ...("person" in payload ? { associatedPerson: payload.person } : {}),
        availableLoading: true,
      },
    },
  }))
  .handleAction(changeAssociatedEntities.success, (state, { payload }) => ({
    ...state,
    recordData: {
      ...state.recordData,
      associations: { ...state.recordData.associations, ...payload, availableLoading: false },
    },
  }))
  .handleAction(changeAssociatedEntities.failure, (state) => ({
    ...state,
    recordData: {
      ...state.recordData,
      associations: { ...state.recordData.associations, availableLoading: false },
    },
  }))
  .handleAction(clearAllUploadedActivityFiles, (state) => ({
    ...state,
    filesAdded: [],
    filesUploading: false,
  }))
  .handleAction(deleteActivity.request, (state) => ({
    ...state,
    deleteLoading: true,
  }))
  .handleAction(deleteActivity.success, (state) => ({
    ...state,
    deleteLoading: false,
  }))
  .handleAction(deleteActivity.failure, (state) => ({
    ...state,
    deleteLoading: false,
  }))
  .handleAction(deleteActivityFile.request, (state, { payload }) => ({
    ...state,
    filesAdded: state.filesAdded.filter((file) => file.uploadedFile?.id !== payload.id),
    filesToDeletedIds: state.filesToDeletedIds.concat(payload.id),
    recordData: {
      ...state.recordData,
      files: state.recordData.files.filter((file) => file.id !== payload.id),
    },
  }))
  .handleAction(deleteActivityFile.success, (state, { payload }) => ({
    ...state,
    filesToDeletedIds: state.filesToDeletedIds.filter((id) => id !== payload.file.id),
    recordData: payload.removed
      ? {
          ...state.recordData,
          files:
            state.recordData.activity?.id === payload.activityId
              ? state.recordData.files.filter((file) => file.id !== payload.file.id)
              : state.recordData.files,
        }
      : {
          ...state.recordData,
          files:
            state.recordData.activity?.id === payload.activityId &&
            !state.recordData.files.some((file) => file.id === payload.file.id)
              ? [...state.recordData.files, payload.file].sort(fileCreatedAtDescComparator)
              : state.recordData.files,
        },
  }))
  .handleAction(deleteActivityFile.failure, (state, { payload }) => ({
    ...state,
    filesToDeletedIds: state.filesToDeletedIds.filter((id) => id !== payload.file.id),
    recordData: {
      ...state.recordData,
      files:
        state.recordData.activity?.id === payload.activityId
          ? [...state.recordData.files, payload.file].sort(fileCreatedAtDescComparator)
          : state.recordData.files,
    },
  }))
  .handleAction(fetchChildActivities.request, (state) => ({
    ...state,
    childActivitiesLoading: true,
  }))
  .handleAction(fetchChildActivities.success, (state, { payload }) => ({
    ...state,
    childActivitiesLoading: false,
    recordData: {
      ...state.recordData,
      childActivities: payload.loadMore
        ? [...state.recordData.childActivities, ...payload.activities]
        : payload.activities,
      childActivitiesTotal: payload.total,
    },
  }))
  .handleAction(fetchChildActivities.failure, (state) => ({
    ...state,
    childActivitiesLoading: false,
  }))
  .handleAction(fetchPreviewData.request, (state) => ({
    ...state,
    activeTab: undefined,
    entityRelating: false,
    entityRelatingSuccess: false,
    hasChanges: false,
    loading: true,
    recordData: INITIAL_RECORD_DATA,
  }))
  .handleAction(fetchPreviewData.success, (state, action) => ({
    ...state,
    hasChanges: false,
    loading: false,
    recordData: action.payload.recordData,
  }))
  .handleAction(fetchPreviewData.failure, (state) => ({
    ...state,
    loading: false,
  }))
  .handleAction(postponeActivity.request, (state) => ({
    ...state,
    postponeLoading: true,
  }))
  .handleAction(postponeActivity.success, (state, { payload }) => ({
    ...state,
    postponeLoading: false,
    recordData: { ...state.recordData, activity: payload },
  }))
  .handleAction(postponeActivity.failure, (state) => ({
    ...state,
    postponeLoading: false,
  }))
  .handleAction(relateUnrelateEntities.request, (state) => ({
    ...state,
    entityRelating: true,
  }))
  .handleAction(
    relateUnrelateEntities.success,
    (state, { payload: { associationsState, deal, person, unrelate } }) => ({
      ...state,
      entityRelating: false,
      entityRelatingSuccess: !unrelate,
      recordData: {
        ...state.recordData,
        associations: {
          ...state.recordData.associations,
          ...associationsState,
          associatedDeal: deal ?? state.recordData.associations.associatedDeal,
          associatedPerson: person ?? state.recordData.associations.associatedPerson,
        },
      },
    })
  )
  .handleAction(relateUnrelateEntities.failure, (state) => ({
    ...state,
    entityRelating: false,
    entityRelatingSuccess: false,
  }))
  .handleAction(removeActivityCompany.success, (state, { payload }) => ({
    ...state,
    recordData: {
      ...state.recordData,
      associations: { ...state.recordData.associations, ...payload, associatedCompany: undefined },
    },
  }))
  .handleAction(removeActivityDeal.success, (state, { payload }) => ({
    ...state,
    recordData: {
      ...state.recordData,
      associations: { ...state.recordData.associations, ...payload, associatedDeal: undefined },
    },
  }))
  .handleAction(setActiveTab, (state, { payload }) => ({
    ...state,
    activeTab: payload,
  }))
  .handleAction(setHasChangesFlag, (state, { payload }) => ({
    ...state,
    hasChanges: payload,
  }))
  .handleAction(toggleCompleteActivity.request, (state) => ({
    ...state,
    toggleCompleteLoading: true,
  }))
  .handleAction(toggleCompleteActivity.success, (state, { payload }) => ({
    ...state,
    recordData: { ...state.recordData, activity: payload },
    toggleCompleteLoading: false,
  }))
  .handleAction(toggleCompleteActivity.failure, (state) => ({
    ...state,
    toggleCompleteLoading: false,
  }))
  .handleAction(updateActivity.request, (state) => ({
    ...state,
    activityUpdateLoading: true,
  }))
  .handleAction(updateActivity.success, (state, { payload }) => ({
    ...state,
    activityUpdateLoading: false,
    recordData: { ...state.recordData, activity: payload },
  }))
  .handleAction(updateActivity.failure, (state) => ({
    ...state,
    activityUpdateLoading: false,
  }))
  .handleAction(uploadActivityFiles.request, (state) => ({
    ...state,
    filesUploading: true,
  }))
  .handleAction(uploadActivityFiles.success, (state, action) => {
    const filesUploaded = compact(action.payload.map(({ uploadedFile }) => uploadedFile));
    return {
      ...state,
      filesAdded: [...state.filesAdded, ...action.payload],
      filesUploading: false,
      recordData: {
        ...state.recordData,
        files: [...state.recordData.files, ...filesUploaded].sort(fileCreatedAtDescComparator),
      },
    };
  })
  .handleAction(uploadActivityFiles.failure, (state) => ({
    ...state,
    filesUploading: false,
  }));

export type ActivityRecordActions = Actions;
export default activityRecord;
